/*
 * WR-024 B3: HorizonMilestoneDialog — the milestone dialog: new or edit.
 * A milestone is EITHER fixed at a week OR placed after rows above it.
 * Save hands the body to the page, which POSTs or PUTs it.
 */

import React, { useState, useEffect, useMemo } from 'react';
import './HorizonDialogs.css';
import { buildRows } from './horizonPlan';

function HorizonMilestoneDialog({ horizon, milestone, onSave, onDelete, onClose }) {
  const [name, setName] = useState('');
  const [mode, setMode] = useState('fixed');
  const [week, setWeek] = useState('');
  const [section, setSection] = useState('');
  const [afterIds, setAfterIds] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (milestone) {
      setName(milestone.name || '');
      setSection(milestone.section || '');
      // Mode: 'after' if week is null and after is non-empty
      const initialMode = milestone.week === null && milestone.after && milestone.after.length > 0 ? 'after' : 'fixed';
      setMode(initialMode);
      if (initialMode === 'fixed') {
        setWeek(String(milestone.week ?? ''));
      } else {
        setWeek('');
      }
      setAfterIds(Array.isArray(milestone.after) ? [...milestone.after] : []);
    } else {
      setName('');
      setMode('fixed');
      setWeek('');
      setSection('');
      setAfterIds([]);
    }
  }, [milestone]);

  const rows = useMemo(() => {
    if (!horizon) return [];
    const allRows = buildRows(horizon, new Set());
    // Filter out section headers (kind === 'section')
    return allRows.filter(r => r.kind !== 'section');
  }, [horizon]);

  const getEligibleRows = () => {
    if (!milestone) {
      // New milestone: all rows are eligible
      return rows;
    }
    // Editing milestone: only rows BEFORE this milestone are eligible
    const seq = rows;
    const idx = seq.findIndex(r => r.id === milestone._id);
    if (idx < 0) return seq;
    return seq.slice(0, idx);
  };

  const eligibleRows = getEligibleRows();

  const handleModeChange = (newMode) => {
    setMode(newMode);
  };

  const handleWeekChange = (value) => {
    setWeek(value);
  };

  const handleSectionChange = (value) => {
    setSection(value);
  };

  const toggleAfterId = (id) => {
    setAfterIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(x => x !== id);
      }
      return [...prev, id];
    });
  };

  const getRowLabel = (row) => {
    if (row.kind === 'milestone') {
      return `◆ ${row.row.name}`;
    }
    const bstId = row.row.snapshot?.bstId || '';
    const num = bstId.replace(/^BST-/, '');
    const title = row.row.snapshot?.title || row.row.snapshot?.bstId || 'Unnamed task';
    return `${num} ${title}`;
  };

  const handleSave = async () => {
    setError('');
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Give the milestone a name.');
      return;
    }

    if (mode === 'fixed') {
      const weekNum = Number(week);
      if (!Number.isInteger(weekNum) || weekNum < 1) {
        setError('Pick a week (1 or later).');
        return;
      }
      try {
        await onSave({ name: trimmedName, week: weekNum, after: [], section: section.trim() });
        onClose();
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    } else {
      if (afterIds.length === 0) {
        setError('Pick at least one row it waits for.');
        return;
      }
      try {
        await onSave({ name: trimmedName, week: null, after: afterIds, section: section.trim() });
        onClose();
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    }
  };

  const handleDelete = async () => {
    if (window.confirm(`Delete milestone "${milestone.name}"?`)) {
      try {
        await onDelete();
        onClose();
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="hzm-body">
          <h3 className="hzm-title">{milestone ? 'Edit milestone' : 'New milestone'}</h3>
          {error && <div className="hzm-error">{error}</div>}

          <div className="hzm-form">
            <div className="hzm-form-group">
              <label>Name</label>
              <input
                className="hzm-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Milestone name"
              />
            </div>

            <div className="hzm-form-group hzm-mode">
              <label>Position</label>
              <div className="hzm-mode-options">
                <label className="hzm-mode-option">
                  <input
                    type="radio"
                    name="hzm-mode"
                    value="fixed"
                    checked={mode === 'fixed'}
                    onChange={(e) => handleModeChange(e.target.value)}
                  />
                  Fixed week
                </label>
                <label className="hzm-mode-option">
                  <input
                    type="radio"
                    name="hzm-mode"
                    value="after"
                    checked={mode === 'after'}
                    onChange={(e) => handleModeChange(e.target.value)}
                  />
                  After rows
                </label>
              </div>
            </div>

            {mode === 'fixed' && (
              <div className="hzm-form-group hzm-week-group">
                <label>Week</label>
                <input
                  className="hzm-week"
                  type="number"
                  min="1"
                  value={week}
                  onChange={(e) => handleWeekChange(e.target.value)}
                  placeholder="1"
                />
              </div>
            )}

            {mode === 'after' && (
              <div className="hzm-form-group hzm-after-group">
                <label>After (order matters)</label>
                <div className="hzm-after-options">
                  {eligibleRows.map(row => (
                    <label key={row.id} className="hzm-after-option">
                      <input
                      type="checkbox"
                      value={row.id}
                      checked={afterIds.includes(row.id)}
                      onChange={() => toggleAfterId(row.id)}
                    />
                      {getRowLabel(row)}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="hzm-form-group">
              <label>Section</label>
              <input
                className="hzm-section"
                type="text"
                value={section}
                onChange={(e) => handleSectionChange(e.target.value)}
                placeholder="Section name"
              />
            </div>
          </div>
        </div>

        <div className="hzm-actions">
          {milestone && (
            <button className="hzm-delete btn btn-secondary" onClick={handleDelete} type="button">
              Delete milestone
            </button>
          )}
          <button className="hzm-cancel btn btn-secondary" onClick={handleCancel} type="button">
            Cancel
          </button>
          <button className="hzm-save btn btn-primary" onClick={handleSave} type="button">
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default HorizonMilestoneDialog;
